import React from 'react';

import { ColumnDef } from '@tanstack/react-table';

import { ITableColumnState } from 'hooks/useTableCurrentState';

import { Risk } from 'api/queries/getRiskRegister.query';

import { OptionValue } from 'components/common/CustomizedStatusSelect/CustomizedStatusSelect.types';
import { DynamicChipsCell } from 'components/common/Tables/Cells/DynamicChipsCell/DynamicChipsCell';
import { columnsCommonConfig } from 'components/common/Tables/columnsCommonConfig';

import { Checkbox } from 'components/DesignSystem/Checkbox';
import { DurationVarianceCell } from 'components/widgets/RiskRegister/components/DurationVarianceCell/DurationVarianceCell';
import { RegisterItemLinkCell } from 'components/widgets/RiskRegister/components/RegisterItemLinkCell/RegisterItemLinkCell';
import { RiskRegisterAssigneeCell } from 'components/widgets/RiskRegister/components/RiskRegisterAssigneeCell/RiskRegisterAssigneeCell';
import { RowOptionsCell } from 'components/widgets/RiskRegister/components/RowOptionsCell/RowOptionsCell';
import { SegmentCell } from 'components/widgets/RiskRegister/components/SegmentCell/SegmentCell';
import { StatusCell } from 'components/widgets/RiskRegister/components/StatusCell/StatusCell';

export const columnsV8: ColumnDef<Risk>[] = [
    {
        ...columnsCommonConfig['select-col'],
        header: ({ table }) => (
            <Checkbox
                checked={table.getIsAllRowsSelected()}
                indeterminate={table.getIsSomeRowsSelected()}
                onChange={table.getToggleAllRowsSelectedHandler()} //or getToggleAllPageRowsSelectedHandler
            />
        ),
        cell: ({ row }) => (
            <Checkbox
                checked={row.getIsSelected()}
                disabled={!row.getCanSelect()}
                onChange={row.getToggleSelectedHandler()}
            />
        ),
    },
    {
        header: 'Name',
        accessorKey: 'name',
        id: 'registerName',
        filterFn: 'includesString',
        sortingFn: 'text',
        cell: ({ getValue, row }) => {
            return <RegisterItemLinkCell tab={'details'} id={row.original.id} title={getValue<string>()} />;
        },
    },
    {
        header: 'Status',
        accessorKey: 'status',
        id: 'status',
        filterFn: (row, columnId, filterValue) => {
            const value = row.getValue(columnId);
            return filterValue.length > 0 ? filterValue.includes(value) : true;
        },
        cell: ({ getValue, row }) => {
            return <StatusCell status={getValue()} riskId={row.original.id} />;
        },
        size: 100,
    },
    {
        header: 'Probability',
        accessorKey: 'probability',
        id: 'probability',
        filterFn: (row, columnId, filterValue) => {
            return filterValue.length > 0 ? filterValue.includes(row.getValue(columnId)) : true;
        },
        cell: ({ getValue }) => {
            return probabilityOptions.find((item) => item.value === getValue())?.label;
        },
        size: 100,
    },
    {
        header: 'Distribution',
        accessorFn: (originalRow) => distributionOptions.find((item) => item.value === originalRow.distribution)?.label,
        id: 'distribution',
        size: 100,
    },
    {
        header: 'Duration Variance',
        accessorKey: 'durationVariance',
        id: 'durationVariance',
        cell: ({ getValue, row }) => {
            const value = getValue<Risk['durationVariance']>();
            const unit = row.original.impactTypeUnit;
            return <DurationVarianceCell value={value} unit={unit} />;
        },
        filterFn: (row, columnId, filterValue) => {
            const value = row.getValue<Record<string, number>>(columnId);
            const impactType = row.original.impactType;
            if (filterValue.length === 1 && filterValue[0] === 'opportunities') {
                return (
                    (impactType === 'ABSOLUTE' &&
                        Object.values(value)
                            .filter((item) => item !== null)
                            .some((item) => item < 0)) ||
                    (impactType === 'RELATIVE' &&
                        Object.values(value)
                            .filter((item) => item !== null)
                            .some((item) => item < 100))
                );
            }

            if (filterValue.length === 1 && filterValue[0] === 'risks') {
                return (
                    (impactType === 'ABSOLUTE' &&
                        Object.values(value)
                            .filter((item) => item !== null)
                            .some((item) => item > 0)) ||
                    (impactType === 'RELATIVE' &&
                        Object.values(value)
                            .filter((item) => item !== null)
                            .some((item) => item > 100))
                );
            }
            if (filterValue.length === 2 || filterValue.length === 0) return true;
            return true;
        },
    },
    {
        header: 'Assignee',
        accessorKey: 'assignee',
        id: 'assignee',
        cell: ({ getValue, row }) => {
            const value = getValue<Risk['assignee']>();

            return <RiskRegisterAssigneeCell assignee={value} riskId={row.original.id} />;
        },
        size: 80,
    },
    {
        header: () => (
            <div>
                Linked
                <br /> Activities
            </div>
        ),
        meta: {
            columnLabel: 'Linked Activities',
        },
        accessorKey: 'linkedActivities',
        id: 'linkedActivities',
        cell: ({ getValue, row }) => {
            return (
                <RegisterItemLinkCell
                    tab={'linkedActivities'}
                    id={row.original.id}
                    title={String(getValue<number>())}
                />
            );
        },
        size: 80,
    },
    {
        header: 'Segment',
        accessorKey: 'activitiesByKey',
        id: 'segment',
        cell: ({ getValue, row }) => {
            const activitiesByKey = getValue<Risk['activitiesByKey']>();
            const linkOption = row.original.linkOption;
            return <SegmentCell activitiesByKey={activitiesByKey} linkOption={linkOption} />;
        },
        size: 250,
    },
    {
        header: 'Tags',
        accessorKey: 'tags',
        id: 'tags',
        filterFn: (row, columnId, filterValue) => {
            const value = row.getValue<Record<string, number>[]>(columnId).map((item) => item.id);
            return filterValue.length > 0 ? filterValue.some((item) => value.includes(item)) : true;
        },
        cell: ({ getValue }) => {
            const value = getValue<Risk['tags']>();
            const list = value.map((item) => item.name);
            return list.length > 0 ? <DynamicChipsCell list={list} /> : null;
        },
    },
    {
        header: '',
        ...columnsCommonConfig.rowOptions,
        size: 50,
        cell: ({ row }) => {
            return <RowOptionsCell riskId={row.original.id} name={row.original.name} />;
        },
    },
];

export const initialState: ITableColumnState = {
    hiddenColumns: [],
    sortBy: [{ id: 'registerName', desc: false }],
};

export const statusOptions: OptionValue[] = [
    { id: '1', name: 'NEW', label: 'New' },
    { id: '2', name: 'OPEN', label: 'Open' },
    { id: '3', name: 'CLOSED', label: 'Closed' },
];

export const probabilityOptions: ISelectOption<Risk['probability']>[] = [
    { value: 'VERY_LOW', label: 'Very Low (10%)' },
    { value: 'LOW', label: 'Low (25%)' },
    { value: 'MEDIUM', label: 'Medium (50%)' },
    { value: 'HIGH', label: 'High (75%)' },
    { value: 'VERY_HIGH', label: 'Very High (90%)' },
    { value: 'CERTAIN', label: 'Certain (100%)' },
];

export type Risks = 'risks' | 'opportunities';

export const risksOptions: ISelectOption<Risks>[] = [
    { value: 'risks', label: 'Risks' },
    { value: 'opportunities', label: 'Opportunities' },
];

export const distributionOptions: ISelectOption<Risk['distribution']>[] = [
    { value: 'UNIFORM', label: 'Uniform' },
    { value: 'TRIANGLE', label: 'Triangle' },
    { value: 'BETA_PERT', label: 'Beta PERT' },
];

export const impactTypeOptions: ISelectOption<Risk['impactType']>[] = [
    { value: 'RELATIVE', label: 'Relative (%)' },
    { value: 'ABSOLUTE', label: 'Absolute (+days)' },
];

export const exportCsvHeaders = [
    {
        key: 'registerName',
        name: 'risk_name',
    },
    {
        key: 'description',
        name: 'description',
    },
    {
        key: 'status',
        name: 'risk_status',
    },
    {
        key: 'probability',
        name: 'probability',
    },
    {
        key: 'distribution',
        name: 'distribution',
    },
    {
        key: 'impactType',
        name: 'impact_type',
    },
    {
        key: 'durationVariance.min',
        name: 'min',
    },
    {
        key: 'durationVariance.likely',
        name: 'most_likely',
    },
    {
        key: 'durationVariance.max',
        name: 'max',
    },
    {
        key: 'linkedActivities',
        name: 'linked_activities',
    },
    {
        key: 'segment',
        name: 'segment_name',
    },
    {
        key: 'assignee',
        name: 'assignee',
    },
];
